import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslate } from "../../Hooks/useTranslate";
import { getData, postData } from "../../adminApi/apiClient";
import useFormChange from "../../Hooks/useFormChange";
import { validateForm } from "../../utils/validation";
import { NewsletterRules } from "../../validation/ValidationRules";
import Spinner from "Common/Globle/Spinner";
import RainbowDiuLogo from "./RainbowDiuLogo";


const initialNewsletterValues = { email: '' };

const Footer = () => {
  const { t } = useTranslate();
  const currentYear = new Date().getFullYear();
  const [footerData, setFooterData] = useState(null);
  const { values, errors, handleChange, setValues, setErrors } = useFormChange(
    initialNewsletterValues,
    NewsletterRules
  );
  const [subscribeStatus, setSubscribeStatus] = useState({ type: null, message: '' });
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await getData('/homepage/footer-info');
        if (response.success && response.data) {
          setFooterData(response.data);
          console.log('Footer data fetched successfully:', response.data);
        }
      } catch (error) {
        console.error('Footer data fetch error:', error);
      }
    };

    fetchFooterData();
  }, []);


  const handleSubscribe = async (e) => {
    e.preventDefault();
    setSubscribeStatus({ type: null, message: '' });

    // Validate form
    const validationErrors = validateForm(values, NewsletterRules);
    setErrors(validationErrors);

    // Stop if there are validation errors
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await postData('/subscribe-emaillist/create', { email: values.email });

      if (response.success) {
        setSubscribeStatus({ type: 'success', message: response.message || t('subscribed_successfully') });
        setValues(initialNewsletterValues);
        setErrors({});
      } else {
        setSubscribeStatus({ type: 'error', message: response.message || t('subscription_failed') });
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setSubscribeStatus({ type: 'error', message: error.message || t('subscription_failed') });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <React.Fragment>
      {isLoading && <Spinner isSpinner={true} />}
      <footer className="guest-footer">
        <div className="container">
          <div className="row g-4 mb-4 mb-md-5 pb-lg-4">
            {/* Brand Section */}
            <div className="col-lg-4 col-md-6 footer-brand pe-lg-5">
              <Link to="/" className="footer-logo">
                <RainbowDiuLogo width={40} height={40} />
                <span className="logo-text">
                  Rainbow Resort
                </span>
              </Link>
              <p className="brand-text mb-3">
                {t('Rainbow Resort is your destination for comfortable hotel stays, quality rooms, modern amenities, and warm hospitality. Enjoy a relaxing and convenient stay with thoughtfully designed accommodation for business and leisure travelers.')}
              </p>
              <div className="social-links">
                {footerData?.social_links && footerData.social_links.length > 0 && (
                  footerData.social_links.map((social, index) => (
                    <a key={index} href={social.url} target="_blank" rel="noopener noreferrer">
                      {/* Use icon as image - no default icons */}
                      {social.icon ? (
                        <img
                          src={social.icon}
                          alt={social.name || 'Social'}
                          style={{ width: '24px', height: '24px', objectFit: 'cover', borderRadius: '4px' }}
                        />
                      ) : null}
                    </a>
                  ))
                )}
              </div>
            </div>

            {/* Quick Links */}
            <div className="col-lg-2 col-md-3 col-6 footer-links">
              <h4 className="footer-title">{t('quick_links')}</h4>
              <ul className="list-unstyled">
                <li><Link to="/">{t('home')}</Link></li>
                <li><Link to="/rooms">{t('rooms')}</Link></li>
                <li><Link to="/blog">{t('blog')}</Link></li>
                <li><Link to="/about">{t('about_us')}</Link></li>
                <li><Link to="/contact">{t('contact')}</Link></li>
              </ul>
            </div>

            {/* Services */}
            <div className="col-lg-2 col-md-3 col-6 footer-links">
              <h4 className="footer-title">{t('services')}</h4>
              <ul className="list-unstyled">
                {footerData?.services && footerData.services.length > 0 && (
                  footerData.services.map((service, index) => (
                    <li key={index}>
                      <a href={service.url} rel="noopener noreferrer">
                        {service.title}
                      </a>
                    </li>
                  ))
                )}
              </ul>
            </div>

            {/* Contact Info */}
            <div className="col-lg-4 col-md-12 footer-contact">
              <h4 className="footer-title">{t('get_in_touch')}</h4>
              {footerData?.contact && footerData.contact.length > 0 && (
                footerData.contact.map((item, index) => (
                  <div className="contact-item" key={index}>
                    {/* Use icon as image - no default icons */}
                    {item.icon ? (
                      <img
                        src={item.icon}
                        alt={item.label || 'Contact'}
                        style={{ width: '20px', height: '20px', objectFit: 'cover' }}
                      />
                    ) : null}
                    <span>{item.value}</span>
                  </div>
                ))
              )}

              {/* Newsletter Section */}
              <div className="footer-newsletter-compact mt-4">
                <h6 className="newsletter-title-sm mb-2">{t('subscribe_to_newsletter')}</h6>
                <form className="newsletter-form-sm d-flex gap-2 align-items-start" onSubmit={handleSubscribe}>
                  <div className="flex-grow-1">
                    <input
                      type="email"
                      name="email"
                      className={`form-control form-control-sm ${errors.email ? 'is-invalid' : ''}`}
                      placeholder={t('enter_email_address')}
                      value={values.email}
                      onChange={handleChange}
                    />
                    {errors.email && <div className="invalid-feedback small">{errors.email}</div>}
                  </div>
                  <button type="submit" className="btn btn-primary btn-sm" disabled={isLoading}>
                    {t('subscribe')}
                  </button>
                </form>
                {subscribeStatus.type && (
                  <div className={`mt-2 small ${subscribeStatus.type === 'success' ? 'text-success' : 'text-danger'}`}>
                    {subscribeStatus.message}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="footer-bottom border-top py-4">
            <div className="row align-items-center text-center text-md-start">
              <div className="col-md-6 mb-3 mb-md-0">
                <p className="mb-0 text-muted small">&copy; {currentYear} Rainbow Resort {t('all_rights_reserved')}</p>
              </div>
              <div className="col-md-6">
                <div className="footer-legal d-flex justify-content-center justify-content-md-end gap-4">
                  {footerData?.terms_privacy && footerData.terms_privacy.length > 0 ? (
                    footerData.terms_privacy.map((item, index) => (
                      <Link
                        key={index}
                        to={`/page/${item.url}`}
                        className="text-muted text-decoration-none small"
                      >
                        {item.label}
                      </Link>
                    ))
                  ) : (
                    <>
                      <Link to="/privacy-policy" className="text-muted text-decoration-none small">{t('privacy_policy', 'Privacy Policy')}</Link>
                      <Link to="/terms-conditions" className="text-muted text-decoration-none small">{t('terms_of_service', 'Terms of Service')}</Link>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </React.Fragment>
  );
};

export default Footer;
